import Head from "next/head";
import languages from "config/languages";
import config from "config/config";
import { useLanguage } from "contexts/LocalizationContext";
import { usePageName, useCMSLanguageData } from "contexts/DataContext/hooks";
import { OG_IMAGE_DIMENSIONS, DEFAULT_FEATURED_IMAGE } from "config/headConsts";
import { getTwitterUsername } from "utils/socialUtils";
import { getHreflangs } from "utils/getHreflangs";
import formatTitle from "utils/formatTitle";
import { useExtendedRouter } from "hooks";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import type { CustomHeadProps } from "./CustomHead.d";
import { createCanonical } from "./utils";

const SEO_TITLE_BRAND_SUFFIX = " | AirHelp";

const CustomHead = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImageUrl = DEFAULT_FEATURED_IMAGE,
  preloadImages = [],
  noindex = false,
  brandTitle = true,
}: CustomHeadProps) => {
  const language = useLanguage();
  const domain = getDomainFromLanguage(language);
  const { asPath: pagePath, isHomepage } = useExtendedRouter();
  const pageName = usePageName();
  const cmsLanguagesData = useCMSLanguageData();
  const hreflangs = getHreflangs(cmsLanguagesData, pageName);
  const ogLocale = languages.get(language)?.hreflang.replace("-", "_");
  const ogUrl = `${domain}${pagePath}`;

  const shouldTitleBeBranded = isHomepage ? false : brandTitle;

  const seoTitle = formatTitle(title, shouldTitleBeBranded, SEO_TITLE_BRAND_SUFFIX);
  const socialMediaTitle = ogTitle ? formatTitle(ogTitle, shouldTitleBeBranded, SEO_TITLE_BRAND_SUFFIX) : seoTitle;

  const socialMediaDescription = ogDescription || description;

  const canonical = createCanonical(language, hreflangs);

  return (
    <Head>
      {preloadImages &&
        preloadImages.length &&
        preloadImages.map((link) => <link key={link} rel="preload" href={`${config.staticUrl}${link}`} as="image" />)}

      {hreflangs &&
        Object.keys(hreflangs).map((languageData) => {
          const { languageCode, href } = hreflangs[languageData];
          const hrefWithTrailingSlash = href.replace(/\/?$/, "/");
          const hrefDomain = getDomainFromLanguage(languageCode);

          return (
            <link
              rel="alternate"
              hrefLang={languages.get(languageCode).hreflang}
              href={`${hrefDomain}${hrefWithTrailingSlash}`}
              key={languageCode}
            />
          );
        })}

      {canonical && <link rel="canonical" href={canonical} />}

      <title>{seoTitle}</title>
      <meta name="description" content={description} />

      {noindex && <meta name="robots" content="noindex" />}

      <meta property="og:locale" content={ogLocale} />
      <meta property="og:type" content="website" />

      <meta property="og:title" content={socialMediaTitle} />
      <meta property="og:description" content={socialMediaDescription} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content="AirHelp" />

      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      <meta property="og:image:width" content={OG_IMAGE_DIMENSIONS.width} />
      <meta property="og:image:height" content={OG_IMAGE_DIMENSIONS.height} />
      <meta property="og:image:alt" content={seoTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:description" content={socialMediaDescription} />
      <meta name="twitter:title" content={socialMediaTitle} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:site" content={getTwitterUsername(language)} />
      <meta name="twitter:creator" content={getTwitterUsername(language)} />
    </Head>
  );
};

export default CustomHead;
